/**
 * Turbo API v3 candidate profile.
 *
 * `job.profile` holds Turbo's GetCandidateProfile JSON as-is: { candidateProfile: [field, ...] }.
 * The v2 shape (an object keyed by field, read positionally by the apply pages) is still
 * supported there; anything with a `candidateProfile` array goes through this module instead.
 *
 * Form values live in flat top-level Formik keys (`cpv3_<fieldId>`), never nested objects,
 * because the apply pages' error summary prints each top-level error as a string.
 */

export const CP_V3_PREFIX = "cpv3_";

// Turbo always requires these when it returns them, whatever `required` says.
const ALWAYS_REQUIRED_FIELDS = ["howheards", "employed"];

export type CpV3FieldType = "singleselect" | "multiselect" | "text" | "textarea";

const SUPPORTED_TYPES: string[] = ["singleselect", "multiselect", "text", "textarea"];

export interface CpV3Option {
  id: string;
  label: string;
  parentId: string | null;
}

export interface CpV3SubField {
  fieldId: string;
  options: CpV3Option[];
  // true: options belong to one parent each (categories → subCategory, pick one or more per parent).
  // false: every option applies to every parent (languages → level, pick exactly one per parent).
  perParent: boolean;
}

export interface CpV3Field {
  fieldId: string;
  key: string;
  label: string;
  type: CpV3FieldType;
  required: boolean;
  options: CpV3Option[];
  subField: CpV3SubField | null;
  sequence: number;
}

export const isCandidateProfileV3 = (profile: any): boolean => {
  return Array.isArray(profile?.candidateProfile);
};

// Turbo HTML-escapes its text ("Office Support &amp; Admin"). Option labels are rendered as
// plain text, so decode them; a <textarea> decodes entities without parsing any markup.
const decodeEntities = (text: string): string => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

// jobTypes, locations and languages carry their text in `name`; every other field uses `value`.
const toOption = (option: any): CpV3Option => {
  return {
    id: String(option?.id),
    label: decodeEntities(String(option?.value ?? option?.name ?? "")),
    parentId: option?.parentId === null || option?.parentId === undefined ? null : String(option.parentId),
  };
};

// `required` is a boolean for most fields but 1 or 2 for howheards — treat any of them as required.
const isRequired = (field: any): boolean => {
  return field?.required === true || Number(field?.required) > 0 || ALWAYS_REQUIRED_FIELDS.includes(field?.fieldId);
};

export const normalizeCandidateProfileV3 = (profile: any): CpV3Field[] => {
  if (!isCandidateProfileV3(profile)) {
    return [];
  }
  return profile.candidateProfile
    .filter((field: any) => field?.fieldId && SUPPORTED_TYPES.includes(field?.type))
    .map((field: any) => {
      const subOptions: CpV3Option[] = Array.isArray(field.subField?.subOptions)
        ? field.subField.subOptions.map(toOption)
        : [];
      return {
        fieldId: String(field.fieldId),
        key: CP_V3_PREFIX + field.fieldId,
        label: field.label || field.fieldId,
        type: field.type,
        required: isRequired(field),
        options: Array.isArray(field.options) ? field.options.map(toOption) : [],
        subField: field.subField
          ? {
            fieldId: String(field.subField.fieldId),
            options: subOptions,
            perParent: subOptions.some((option) => option.parentId !== null),
          }
          : null,
        sequence: Number(field.sequence) || 0,
      };
    })
    .sort((a: CpV3Field, b: CpV3Field) => a.sequence - b.sequence);
};

export const getCandidateProfileV3SubKey = (field: CpV3Field, parentId: string): string => {
  return `${field.key}__${field.subField?.perParent ? "sub" : "level"}__${parentId}`;
};

export const getCandidateProfileV3SelectedIds = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map(String);
  }
  return value === undefined || value === null || value === "" ? [] : [String(value)];
};

export const getCandidateProfileV3InitialValues = (fields: CpV3Field[]): Record<string, any> => {
  const values: Record<string, any> = {};
  fields.forEach((field) => {
    values[field.key] = field.type === "multiselect" ? [] : "";
  });
  return values;
};

const isTextField = (field: CpV3Field) => field.type === "text" || field.type === "textarea";

// A selected parent needs a second-level answer whenever it has second-level options.
const parentHasSubOptions = (field: CpV3Field, parentId: string) => {
  if (!field.subField) {
    return false;
  }
  return field.subField.perParent
    ? field.subField.options.some((option) => option.parentId === parentId)
    : field.subField.options.length > 0;
};

// The section's "Complete" tag: every required answer given, and every picked parent has its sub-answer.
export const isCandidateProfileV3Complete = (fields: CpV3Field[], values: Record<string, any>): boolean => {
  return fields.every((field) => {
    const value = values[field.key];
    if (isTextField(field)) {
      return !field.required || (typeof value === "string" && value.trim() !== "");
    }
    const ids = getCandidateProfileV3SelectedIds(value);
    if (field.required && ids.length === 0) {
      return false;
    }
    return ids.every((parentId) => !parentHasSubOptions(field, parentId)
      || getCandidateProfileV3SelectedIds(values[getCandidateProfileV3SubKey(field, parentId)]).length > 0);
  });
};

export interface CpV3Answer {
  fieldId: string;
  ids?: string[];
  children?: Record<string, string[]>;
  text?: string;
}

/**
 * The applicant's answers for submission (`candidateProfileV3`). The backend turns these into
 * Turbo's candidateProfile JSON, checking every id against the client's saved profile.
 * Key names avoid `value` / `attributes`: SubmitJobRequestMiddleware renames those at any depth.
 */
export const buildCandidateProfileV3Answers = (fields: CpV3Field[], values: Record<string, any>): CpV3Answer[] => {
  const answers: CpV3Answer[] = [];
  fields.forEach((field) => {
    const value = values[field.key];
    if (isTextField(field)) {
      const text = typeof value === "string" ? value.trim() : "";
      if (text) {
        answers.push({ fieldId: field.fieldId, text });
      }
      return;
    }
    const ids = getCandidateProfileV3SelectedIds(value);
    if (ids.length === 0) {
      return;
    }
    const answer: CpV3Answer = { fieldId: field.fieldId, ids };
    if (field.subField) {
      const children: Record<string, string[]> = {};
      ids.forEach((parentId) => {
        const subIds = getCandidateProfileV3SelectedIds(values[getCandidateProfileV3SubKey(field, parentId)]);
        if (subIds.length > 0) {
          children[parentId] = subIds;
        }
      });
      if (Object.keys(children).length > 0) {
        answer.children = children;
      }
    }
    answers.push(answer);
  });
  return answers;
};

// Same shape the apply pages use for screening questions: { fieldId, label } per required field.
// The error summary prints these as text, so the label is decoded here too.
export const getCandidateProfileV3RequiredReferences = (fields: CpV3Field[]) => {
  return fields
    .filter((field) => field.required)
    .map((field) => ({ fieldId: field.key, label: decodeEntities(field.label) }));
};
